import React, { useEffect, useRef, useState } from "react"
import "./OrderSummary.scoped.scss"
import { PaymentMethod, PaymentMethodDisplay, PaymentStatus, TransectionErrorType } from "../../../../models/transection"
import { token } from "../../../../config"
import { useLocation, useHistory } from "react-router-dom"
import queryString from "query-string"
import { Spin } from "antd"
import ModalWarning from "../../../../components/Ants/Modal/ModalWarning"
import { apiClient } from "../../../../api"
import { jsPDF } from 'jspdf'
import html2canvas from "html2canvas"
import TaxInvoice from "../../../../components/TaxInvoice/TaxInvoice"

const OrderSummary = () => {
  const location = useLocation()
  const invoiceRef = useRef(null)
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [orderDetail, setOrderDetail] = useState({})
  const [errorMessage, setErrorMessage] = useState("")
  const [openModalError, setOpenModalError] = useState(false)
  const [taxData, setTaxData] = useState({})

  const getOrderDetail = async () => {
    setLoading(true)
    const { oid } = queryString.parse(location.search)
    const req = JSON.stringify({
      oid: oid,
    })

    await apiClient
      .post("api/ecom/get-order-detail", req, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-type": "application/json; charset=UTF-8",
        },
      })
      .then((response) => {
        if (response.data.status == TransectionErrorType.SUCCESS) {
          setOrderDetail(response.data.order)
        } else {
          setErrorMessage(response.data.message)
          setOpenModalError(true)
        }
      })
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const onDownloadInvoice = async (responseType = "arraybuffer") => {
    let url = ""
    let req = JSON.stringify({
      tiid: orderDetail?.tax_invoice_id,
    })

    const res = await apiClient
      .post("api/ecom/download-tax-invoice", req, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-type": "application/json; charset=UTF-8",
        },
        responseType,
      })
      .then((response) => {
        const isEmptyObj = response.data && typeof response.data === "object" &&
          !Array.isArray(response.data) &&
          Object.keys(response.data).length === 0;

        if (!isEmptyObj) {
          const blob = new Blob([response.data], {
            type: "application/octet-stream",
          })
          url = URL.createObjectURL(blob)
          console.log("--->", response.data)
          setTaxData(response.data)
          generatePdf(response.data)
        } else {
          setErrorMessage("Can not download tax invoice")
          setOpenModalError(true)
        }

      })
      .catch((error) => {
        console.log(error)
        return null
      })
      .finally((data) => {
        setLoading(false)
      })
  }

  const generatePdf = async (data) => {
    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2, // Higher scale for better quality
        useCORS: true, // Enable CORS for images
        logging: false,
        allowTaint: true,
      })

      const imgData = canvas.toDataURL("image/png")

      // A4 size in mm: 210 x 297
      const pdf = new jsPDF("p", "mm", "a4")

      const imgWidth = 210
      const pageHeight = 297
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight
      let position = 0

      // First page
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      // Additional pages if needed
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      // Save the PDF
      pdf.save(`${data.receipt_no}.pdf`)

      setLoading(false)
    } catch (error) {
      console.error(error)
    }

  }

  useEffect(() => {
    getOrderDetail()
  }, [])

  return (
    <Spin spinning={loading}>
      <div className="order-summary">
        <div className="order-summary-container">
          <h1 className="order-summary-title">Order</h1>

          {/* Package Detail Section */}
          <div className="order-summary-section">
            <h2 className="section-title">Package Detail</h2>

            <div className="order-summary-row">
              <span className="order-summary-label">Plan</span>
              <span className="order-summary-value order-summary-value-highlight">{orderDetail?.package?.pkg_name}</span>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">Price</span>
              <span className="order-summary-value">{orderDetail?.package?.pkg_price.toLocaleString()} Baht</span>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">Subtotal</span>
              <span className="order-summary-value">{orderDetail?.package?.pkg_total.toLocaleString()} Baht</span>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">Discount</span>
              <span className="order-summary-value">{orderDetail?.package?.pkg_discount.toLocaleString()} Baht</span>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">Fee</span>
              <span className="order-summary-value">{orderDetail?.package?.pkg_with_fee.toLocaleString()} Baht</span>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">VAT 7%</span>
              <span className="order-summary-value">{orderDetail?.package?.pkg_with_vat.toLocaleString()} Baht</span>
            </div>

            <div className="order-summary-row order-summary-row-total">
              <span className="order-summary-label order-summary-label-bold">NET AMOUNT</span>
              <span className="order-summary-value order-summary-value-total">{orderDetail?.package?.pkg_total.toLocaleString()} Baht</span>
            </div>
          </div>

          {/* Order Detail Section */}
          <div className="order-summary-section">
            <h2 className="section-title">Order Detail</h2>

            <div className="order-summary-row">
              <span className="order-summary-label">Receipt NO</span>
              <span className="order-summary-value">{orderDetail?.receipt_no ? orderDetail?.receipt_no : "-"}</span>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">Order Date</span>
              <span className="order-summary-value">{orderDetail?.create_date_display}</span>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">Payment Method</span>
              <div className="order-payment-method">
                {orderDetail?.payment_method_display?.startsWith("Mobile") ? (
                  <img src={`/img/mobile-banking/${PaymentMethodDisplay[orderDetail?.payment_method_display?.toLowerCase()]?.path}.png`} alt="PromptPay" style={{ width: "24px", height: "24px" }} />
                ) : (
                  <img src={`/img/payment-method/${PaymentMethodDisplay[orderDetail?.payment_method_display?.toLowerCase()]?.path}.png`} alt="payment" style={{ width: "24px", height: "24px" }} />
                )}
                <span className="order-summary-value">{PaymentMethodDisplay[orderDetail?.payment_method_display?.toLowerCase()]?.name}</span>
              </div>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">Status</span>
              <span
                className="order-summary-value order-summary-value-status"
                style={{ backgroundColor: PaymentStatus[orderDetail?.payment_status]?.background, color: PaymentStatus[orderDetail?.payment_status]?.color }}
              >
                {PaymentStatus[orderDetail?.payment_status]?.name}
              </span>
            </div>
          </div>

          {/* Tax Invoice Detail Section */}
          <div className="order-summary-section">
            <h2 className="section-title">Tax Invoice Detail</h2>

            <div className="order-summary-row">
              <span className="order-summary-label">Contact name</span>
              <span className="order-summary-value">{orderDetail?.name}</span>
            </div>

            <div className="order-summary-row">
              <span className="order-summary-label">Address</span>
              <span className="order-summary-value">
                {orderDetail?.address ? orderDetail?.address + " " + orderDetail?.subdistrict + " " + orderDetail?.district + " " + orderDetail?.province + " " + orderDetail?.post : "-"}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="order-summary-actions">
            <button className="order-summary-button button-secondary" onClick={() => history.push("/PackageList")}>
              Back
            </button>
            <button
              className="order-summary-button button-primary"
              onClick={onDownloadInvoice}
              style={{ display: PaymentStatus[orderDetail?.payment_status]?.name === "Expired" || PaymentStatus[orderDetail?.payment_status]?.name === "Failed" ? "none" : "block" }}
            >
              Download Tax Invoice
            </button>
          </div>

        </div>
      </div>

      <TaxInvoice invoiceRef={invoiceRef} taxInvoice={taxData} />

      <ModalWarning
        loading={loading}
        warningModalVisible={openModalError}
        warningModal={() => setOpenModalError(false)}
        successModal={() => setOpenModalError(false)}
        modalcountDown={() => setOpenModalError(false)}
        message={errorMessage}
        messageSuccess={""}
      />
    </Spin>
  )
}

export default OrderSummary
